import React, { useState, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Camera,
  AlertCircle,
  Loader2,
  X,
  Factory,
  Image,
  Upload,
  ArrowLeft,
} from "lucide-react";
import { machinesAPI, maintenanceAPI, errorAPI } from "../api";
import toast from "react-hot-toast";
import clsx from "clsx";

const LogError = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null); // Changed to a single file object
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [description, setDescription] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");

  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => res.data.data),
  });

  const uploadMutation = useMutation({
    mutationFn: (photoFile) => {
      const formData = new FormData();
      formData.append("image", photoFile);
      return maintenanceAPI.uploadImage(formData);
    },
  });

  const logMutation = useMutation({
    mutationFn: (data) => errorAPI.log(data),
    onSuccess: () => {
      toast.success(
        "Error logged successfully! A service ticket has been created.",
      );
      navigate("/errors");
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || "Failed to report issue");
    },
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFile = (selectedFile) => {
    if (!selectedFile) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/tiff"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(selectedFile.type)) {
      toast.error(
        "Invalid file type. Please upload a PNG, JPG, or TIFF image.",
      );
      return;
    }
    if (selectedFile.size > maxSize) {
      toast.error("File size must be less than 5MB.");
      return;
    }
    setFile(selectedFile);
    setPreview(URL.createObjectURL(selectedFile));
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFile(e.dataTransfer.files[0]);
      }
    },
    [handleFile],
  );

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
    e.target.value = null;
  };

  const removeFile = () => {
    setFile(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMachine || !description)
      return toast.error("Please select a machine and provide a description.");

    try {
      let imageUrl = null;
      if (file) {
        imageUrl = await toast.promise(uploadMutation.mutateAsync(file), {
          loading: "Uploading photo...",
          success: "Photo uploaded!",
          error: "Photo upload failed.",
        });
        imageUrl = imageUrl.data.url; // Extract URL from response
      }

      await logMutation.mutateAsync({
        machineId: selectedMachine,
        description,
        mediaUrls: imageUrl ? [imageUrl] : [],
      });
    } catch (err) {
      // Errors are handled by react-query's onError and toast.promise
      console.error("Submission failed:", err);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const isPending = logMutation.isPending || uploadMutation.isPending;

  return (
    // Changed pb-12 to pb-8 or pb-6 for less bottom padding
    <div className="max-w-3xl mx-auto space-y-8 pb-8">
      <div className="flex items-center gap-4">
        <Link
          to="/errors"
          className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Report Machine Issue
          </h1>
          <p className="text-slate-500">
            Provide details about the machine error for a quick resolution.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card p-6 space-y-5">
          {" "}
          {/* Could reduce space-y-5 to space-y-4 if needed */}
          <h3 className="font-bold text-lg text-slate-800 border-b pb-3 border-slate-100">
            Error Details
          </h3>
          <div>
            <label className="label font-medium text-slate-700">
              Machine <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Factory className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <select
                className="input pl-10"
                value={selectedMachine}
                onChange={(e) => setSelectedMachine(e.target.value)}
                required
              >
                <option value="">Select Machine...</option>
                {machines?.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} ({m.model})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label font-medium text-slate-700">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              className="input min-h-[120px]"
              placeholder="Describe the problem in detail (e.g., Error code, symptoms, when it started)..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>
          {/* Moved Photo Upload Section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            // Adjusted styling: removed 'card', reduced padding, added mt-4
            className={clsx(
              "p-6 border-2 border-dashed transition-all text-center rounded-xl mt-4",
              dragActive
                ? "border-red-500 bg-red-50 shadow-lg"
                : "border-slate-300 hover:border-slate-400 bg-white",
            )}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept="image/png, image/jpeg, image/tiff"
              onChange={handleFileInput}
              disabled={!!file}
            />
            <label htmlFor="file-upload" className="cursor-pointer block">
              <Camera
                className={clsx(
                  "w-14 h-14 mx-auto mb-4 transition-colors",
                  dragActive ? "text-red-600" : "text-slate-400",
                )}
              />
              <p className="text-xl font-semibold text-slate-800 mb-1">
                {dragActive ? "Drop the photo now" : "Add Evidence Photo"}
              </p>
              <p className="text-sm text-slate-500 mb-4">
                or{" "}
                <span className="text-red-600 font-medium">
                  click to browse
                </span>
              </p>
              <p className="text-xs text-slate-400">
                PNG, JPG, or TIFF (Max 5MB).
              </p>
            </label>
          </motion.div>
          {file && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              // Adjusted styling: removed 'card', added rounded-xl, added mt-4
              className="p-4 flex items-center gap-4 bg-slate-50 border border-slate-200 rounded-xl mt-4"
            >
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                <Image className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 truncate">
                  {file.name}
                </p>
                <p className="text-xs text-slate-500">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <button
                type="button"
                onClick={removeFile}
                className="p-2 text-slate-500 hover:text-red-500 hover:bg-slate-200 rounded-full transition-colors"
                title="Remove file"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <Link to="/errors" className="btn btn-ghost">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={isPending}
            className="btn btn-primary"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Submit Error Log
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LogError;
